import React, { useState } from 'react';
import { Modal, Button, Input, Form, Select, Row, Col, Space, message, Collapse, Upload } from 'antd';
import { PlusOutlined,UploadOutlined } from '@ant-design/icons';
import UploadButton from '../../../../../shared/UploadButton/UploadButton';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  FilterOperatorsEnum, IBrandEntity, IBrandModelEntity, IModelColorsEntity,
  IProcurementDTO,
  IProcurementEntity, IProductTypeEntity, IUserEntity, IVendorEntity,
  ProcurementTypeEnum,
  removeWhiteSpaceFromAllProperties,
} from '@rasayi-workspace/shared';
import { PostItem } from '../../../../../../services';
import { NewSearchableDropdown, SearchableDropdown } from '../../../../../shared';

const { Option } = Select;
const { Panel } = Collapse;

interface ProcurementModalProps {
  visible: boolean;
  onClose: () => void;
  openProcurementModal: () => void;
}

const ProcurementModal: React.FC<ProcurementModalProps> = ({
                                                             visible,
                                                             onClose,
                                                             openProcurementModal,
                                                           }) => {
  const [productCount, setProductCount] = useState(1);

  const [messageApi, contextHolder] = message.useMessage();
  const [vendorId, setVendorId] = useState<string>('');
  const [agentId, setAgentId] = useState<string>('');
  const [procurementType, setProcurementType] = useState<ProcurementTypeEnum | ''>('');
  const [comment, setComment] = useState<string>('');



  const { mutate: createProcurement, isLoading } = useMutation<IProcurementEntity, AxiosError, Partial<IProcurementDTO>>({
    mutationKey: ['createProcurement'],
    mutationFn: async (formData) =>
      PostItem('procurement', (formData)),
    onSuccess: () => {
      messageApi.success('Procurement details saved successfully');
      onClose();
    },
    onError: (error) => {
      messageApi.error(
        (error?.response?.data as any)?.message || 'Failed to save procurement'
      );
    },
  });


  const [products, setProducts] = useState<any[]>([
    {
      productTypeId: '',
      brandId: '',
      brandName: '',
      modelId: '',
      color: '',
      specification: '',
      purchasePrice: '',
      retailPrice: '',
      quantity: 1,
    },
  ]);


  const addProduct = () => {
    setProductCount(productCount + 1);
    setProducts([
      ...products,
      {
        productTypeId: '',
        brand: '',
        brandName: '',
        modelId: '',
        color: '',
        specification: '',
        purchasePrice: '',
        retailPrice: '',
        quantity: 1,
      },
    ]);
  };

  const handleSave = () => {
    const formData = new FormData();

    formData.append("agent_id", agentId);
    formData.append("vendor_id", vendorId);
    formData.append("procurement_type", procurementType);
    formData.append("comment", comment || '');
    formData.append("approved", "false");

    // Attach uploaded file (if available)
    // if (uploadedFile) {
    //   formData.append("images", uploadedFile, uploadedFile.name);
    // }

    products.forEach((product, index) => {
      formData.append(`products[${index}][product_type_id]`, product.productTypeId || '');
      formData.append(`products[${index}][brand_id]`, product.brandId || '');
      formData.append(`products[${index}][model_id]`, product.modelId || '');
      formData.append(`products[${index}][quantity]`, String(product.quantity || 1));
      formData.append(`products[${index}][product_description]`, product.description || '');
      formData.append(`products[${index}][specification]`, product.specification || '');
      formData.append(`products[${index}][purchase_price]`, String(product.purchasePrice || 0));
      formData.append(`products[${index}][retail_price]`, String(product.retailPrice || 0));
      formData.append(`products[${index}][color_id]`, product.colorId || '');
      formData.append(`products[${index}][imei]`, product.imei || '');
      formData.append(`products[${index}][imei2]`, product.imei2 || '');
      formData.append(`products[${index}][product_name]`, product.name || `${product.brand} ${product.model}`);
    });

    createProcurement(formData as any);
  };




  const handleProductChange = (value: any, index: number, field: string) => {
    const newProducts = [...products];
    newProducts[index][field] = value;
    setProducts(newProducts);
  };

  // Calculate Summary
  const calculateSummary = () => {
    const totalProducts = products.length;
    const totalPurchasePrice = products.reduce(
      (acc, product) => acc + (product.purchasePrice || 0) * (product.quantity || 1),
      0
    );
    const totalRetailPrice = products.reduce(
      (acc, product) => acc + (product.retailPrice || 0) * (product.quantity || 1),
      0
    );
    return { totalProducts, totalPurchasePrice, totalRetailPrice };
  };

  const { totalProducts, totalPurchasePrice, totalRetailPrice } = calculateSummary();

  return (
    <Modal
      title="Procurement"
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1171}
      destroyOnClose
      centered
      style={{ height: '818px' }}
    >
      <Form layout="vertical">
        <Row gutter={16}>
          <Col span={16}>
            <div className="flex flex-col h-[636px] overflow-hidden">
              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <h3 className="form-section-heading">Basic Details</h3>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Agent" required className="form-item-tight">
                      <NewSearchableDropdown<IUserEntity>
                        keyProp="id"
                        valueProp="first_name"
                        queryKey={['getUsers']}
                        queryFnParams={{
                          apiRoute: 'user',
                          relations: [],
                          fields: ['id', 'first_name', 'last_name'],
                          filter: [
                            {
                              field: 'first_name',
                              operator: FilterOperatorsEnum.LIKE,
                              value: ''
                            }
                          ],
                          predefinedFilters: []
                        }}
                        onSelectionChange={(selectedId, option, items) => {
                          if (!Array.isArray(selectedId)) {
                            setAgentId(selectedId);
                            console.log(selectedId);
                            console.log(option);
                          }
                        }}
                      />

                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Vendor" required className="form-item-tight">
                      <NewSearchableDropdown<IVendorEntity>
                        keyProp="id"
                        valueProp="vendor_name"
                        queryKey={['getVendors']}
                        queryFnParams={{
                          apiRoute: 'vendor',
                          relations: [],
                          fields: ['id', 'first_name', 'last_name', 'vendor_name'],
                          filter: [
                            {
                              field: 'vendor_name',
                              operator: FilterOperatorsEnum.LIKE,
                              value: ''
                            }
                          ],
                          predefinedFilters: []
                        }}
                        onSelectionChange={(selectedId: string | string[], option, items) => {
                          if (!Array.isArray(selectedId)) {
                            setVendorId(selectedId);
                            console.log('-----------------------');
                            console.log('Selected Vendor ID:', selectedId);
                            console.log('Selected Option:', option);
                            console.log('All Items:', items);
                          }
                        }}
                      />

                    </Form.Item>

                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Procurement Type" required className="form-item-tight">
                      <Select
                        placeholder="Select Procurement Type"
                        value={procurementType}
                        onChange={(value) => setProcurementType(value)} // store selected value
                      >
                        {Object.entries(ProcurementTypeEnum).map(([key, value]) => (
                          <Option key={key} value={value}>
                            {key.charAt(0) + key.slice(1).toLowerCase()}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Comment" className="form-item-tight">
                      <Input
                        placeholder="Add Comment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                      />
                    </Form.Item>

                  </Col>
                </Row>

                <Row className="no-vertical-spacing">
                  <Col span={24} className={'flex'}>
                    <div style={{
                      width: '100%',
                      height: '45px',
                      display: 'flex',
                      padding: 0,
                      margin: 0,
                    }}>
                      <UploadButton />
                    </div>
                  </Col>
                </Row>

                <h3 className="form-section-heading">Product ({productCount})</h3>

                {[...Array(productCount)].map((_, index) => (
                  <div key={index}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Product Type" required className="form-item-tight">
                          <NewSearchableDropdown<IProductTypeEntity>
                            keyProp="id"
                            valueProp="name"
                            queryKey={['getProductTypes']}
                            queryFnParams={{
                              apiRoute: 'product_type',
                              relations: [],
                              fields: ['id', 'name'],
                              filter: [
                                {
                                  field: 'name',
                                  operator: FilterOperatorsEnum.LIKE,
                                  value: ''
                                }
                              ],
                              predefinedFilters: []
                            }}
                            onSelectionChange={(selectedId: string | string[], option) => {
                              if (!Array.isArray(selectedId)) {
                                handleProductChange(selectedId, index, 'productTypeId');
                                console.log('Selected Product Type ID:', selectedId);
                                console.log('Selected Option:', option);
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Brand" required className="form-item-tight">
                          <NewSearchableDropdown<IBrandEntity>
                            keyProp="id"
                            valueProp="name"
                            queryKey={['getBrands']}
                            queryFnParams={{
                              apiRoute: 'brand',
                              relations: [],
                              fields: ['id', 'name'],
                              filter: [
                                {
                                  field: 'name',
                                  operator: FilterOperatorsEnum.LIKE,
                                  value: ''
                                }
                              ],
                              predefinedFilters: []
                            }}
                            onSelectionChange={(selectedId: string | string[], option) => {
                              if (!Array.isArray(selectedId)) {
                                handleProductChange(selectedId, index, 'brandId');
                                handleProductChange((option as any)?.children ?? '', index, 'brandName');

                                console.log('Products State:', products);
                                console.log('Selected brandName:', products[index]?.brandName);
                              }
                            }}
                          />

                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Model" required className="form-item-tight">
                          <NewSearchableDropdown<IBrandModelEntity>
                            keyProp="id"
                            valueProp="name"
                            queryKey={['getModels', products[index]?.brandName]}
                            queryFnParams={{
                              apiRoute: 'brand_model',
                              relations: ['brand'],
                              fields: ['id', 'name'],
                              filter: [
                                {
                                  field: 'brand.name',
                                  operator: FilterOperatorsEnum.EQUAL,
                                  value: products[index]?.brandName
                                }
                              ],
                              predefinedFilters: []
                            }}
                            enabled={!!products[index]?.brandName}
                            onSelectionChange={(selectedId, option, items) => {
                              if (!Array.isArray(selectedId)) {
                                console.log('Selected model ID:', selectedId);
                                handleProductChange(selectedId, index, 'modelId');
                                console.log('Updated products:', products);
                              }
                            }}
                          />

                        </Form.Item>

                      </Col>
                      <Col span={12}>
                        <Form.Item label="Quantity" required className="form-item-tight">
                          <Input
                            value={products[index].quantity}
                            onChange={(e) => handleProductChange(Number(e.target.value), index, 'quantity')}
                            placeholder="Enter Quantity"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Specification" required className="form-item-tight">
                          <Input
                            value={products[index].specification}
                            onChange={(e) => handleProductChange(e.target.value, index, 'specification')}
                            placeholder="Enter Specification"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Color" required className="form-item-tight">
                          <NewSearchableDropdown<IModelColorsEntity>
                            keyProp="id"
                            valueProp="color_name"
                            queryKey={['getModelColors']}
                            queryFnParams={{
                              apiRoute: 'model_color',
                              relations: [],
                              fields: ['id', 'color_name', 'hex_code'],
                              filter: [
                                {
                                  field: 'color_name',
                                  operator: FilterOperatorsEnum.LIKE,
                                  value: ''
                                }
                              ],
                              predefinedFilters: []
                            }}
                            onSelectionChange={(selectedId: string | string[], option, items) => {
                              if (!Array.isArray(selectedId)) {
                                handleProductChange(selectedId, index, 'colorId'); // must use colorId
                                console.log('Selected Color ID:', selectedId);
                              }
                            }}
                          />

                        </Form.Item>

                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Purchase Price" required className="form-item-tight">
                          <Input
                            value={products[index].purchasePrice}
                            onChange={(e) => handleProductChange(Number(e.target.value), index, 'purchasePrice')}
                            placeholder="Enter Purchase Price"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Retail Price" required className="form-item-tight">
                          <Input
                            value={products[index].retailPrice}
                            onChange={(e) => handleProductChange(Number(e.target.value), index, 'retailPrice')}
                            placeholder="Enter Retail Price"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                ))}
              </div>
            </div>
          </Col>

          {/* Product Details Section */}
          <Col span={8}>
            <div className="flex flex-col h-[636px] bg-[#FAFAFA] p-2 rounded-lg overflow-hidden">
              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <h3 className="form-section-heading">Product Details</h3>

                <Collapse
                  defaultActiveKey={['1']}
                  bordered={false}
                  className="bg-white"
                  expandIconPosition="right"
                >
                  {products.map((product, index) => (
                    <Panel header={`Product ${index + 1}`} key={index}>
                      {/* Product Info */}
                      {[
                        ['Product Type', product.productType],
                        ['Brand', product.brand],
                        ['Model', product.model],
                        ['Color', product.color],
                        ['Purchase Price', product.purchasePrice],
                        ['Retail Price', product.retailPrice],
                      ].map(([label, value], idx) => (
                        <Row gutter={16} key={idx}>
                          <Col span={12} style={{ textAlign: 'left' }}>
                            <strong>{label}:</strong>
                          </Col>
                          <Col span={12} style={{ textAlign: 'right' }}>
                            {value}
                          </Col>
                        </Row>
                      ))}
                    </Panel>
                  ))}
                </Collapse>
              </div>

              <div className="mt-auto">
                <h3 className="form-section-heading">Product Summary</h3>
                <Row>
                  <Col span={24}>
                    <div><strong>Total Products:</strong> {totalProducts}</div>
                    <div><strong>Total Purchase Price:</strong> {totalPurchasePrice}</div>
                    <div><strong>Total Retail Price:</strong> {totalRetailPrice}</div>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>

        {/* Footer Buttons */}
        <div className="flex justify-between mt-4">
          <Button
            onClick={addProduct}
            icon={<PlusOutlined />}
            className="w-[191px] h-[40px] mb-4 text-[#BD273B] !border-[#BD273B] bg-white hover:bg-[#BD273B] hover:text-white hover:border-[#BD273B]"
          >
            Add New Product
          </Button>

          <Space>
            <Button onClick={onClose} className="h-[40px]">
              Cancel
            </Button>
            <Button type="primary" onClick={handleSave} className="h-[40px]">
              Save
            </Button>
          </Space>
        </div>
      </Form>

    </Modal>
  );
};

export default ProcurementModal;
